import axios from '../axios';
import { apiPaths } from '../helper/apiPaths';
import { errMsg } from '../helper/functions';
import { errMsgNoData } from '../helper/functions';
import { ErrorResponseType } from '../types';

const hrDashboardStats = import.meta.env.VITE_GET_HR_DASHBOARD_STATS;

export const getHRDashboardStatus = async (year: number) => {
  const ret: {
    data: ResponseHRDashboard | null;
    error: ErrorResponseType | null;
  } = {
    data: null,
    error: null,
  };
  try {
    const { data } = await axios.get(
      `${apiPaths.hrDashboardStats}/${year}?code=${hrDashboardStats}`
    );
    if (data) {
      ret.data = data;
    } else {
      ret.error = errMsgNoData();
    }
    return ret;
  } catch (err) {
    ret.error = errMsg(err);
    return ret;
  }
};

const getHRDashboardStatusFilterCode = import.meta.env
  .VITE_GET_HR_DASHBOARD_STATS_FILTER;

export const getHRDashboardStatusFilter = async (
  year: number,
  disciplineId: number,
  tier: number
) => {
  const ret: {
    data: ResponseHRDashboard['overallCompetencyAnalysis'] | null;
    error: ErrorResponseType | null;
  } = {
    data: null,
    error: null,
  };
  try {
    const { data } = await axios.get(
      `${apiPaths.hrDashboardStatsFilter}/${year}?code=${getHRDashboardStatusFilterCode}&discipline=${disciplineId}&tier=${tier}`
    );

    if (data) {
      ret.data = data;
    } else {
      ret.error = errMsgNoData();
    }
    return ret;
  } catch (err) {
    ret.error = errMsg(err);
    return ret;
  }
};

export interface ResponseHRDashboard {
  annualReviewSubmissionStatus: AnnualReviewSubmissionStatus;
  activeObjectivesStatus: ObjectivesStatus;
  developmentObjectivesStatus: ObjectivesStatus;
  overallCompetencyAnalysis: OverallCompetencyAnalysis;
  averageScoreAcrossDisciplines: AverageScoreAcrossDisciplines;
  totalQuarterlyFeedbackDeadlinesMissedCount: TotalQuarterlyFeedbackDeadlinesMissedCount;
}
// Mentor Stats Interface
interface MentorStats {
  mentorEmail: string;
  mentorName: string;
  totalReviews: number;
  completedReviews: number;
  pendingReviews: number;
  completedMenteesList: string[];
  pendingMenteesList: string[];
}

// Annual Review Status Interface
interface AnnualReviewSubmissionStatus {
  totalReviews: number;
  completedReviews: number;
  pendingReviews: number;
  mentorStats: MentorStats[];
}

// Mentee List Item Interface
interface MenteeListItem {
  menteeName: string;
  objectiveCount: number;
}

// Mentor Objectives Interface
interface MentorObjectives {
  mentorEmail: string;
  mentorName: string;
  totalObjectives: number;
  completedObjectives: number;
  inProgressObjectives: number;
  notStartedObjectives: number;
  draftObjectives: number;
  completedMenteeList: MenteeListItem[];
  notStartedMenteeList: MenteeListItem[];
}

// Objectives Status Interface
interface ObjectivesStatus {
  totalObjectives: number;
  completedObjectives: number;
  inProgressObjectives: number;
  notStartedObjectives: number;
  draftObjectives: number;
  mentorObjectives: MentorObjectives[];
}

// Competency Score Interface
interface CompetencyExpectScore {
  tier: number;
  disciplineId: number;
  competencyName: string;
  expectedScore: number;
}

// Competency Analysis Interface
interface OverallCompetencyAnalysis {
  competencyExpectScores: CompetencyExpectScore[];
  averageCompetencyScoreAcrossDisciplinesTiers: AverageCompetencyScoreAcrossDisciplinesTiers[];
  competencies: {
    statementId: number;
    competencyName: string;
  }[];
  menteeCompetencyPerformances: any[]; // Type can be specified based on actual data
  competencyLevelPerformance: CompetencyLevelPerformance[]; // Type can be specified based on actual data
  discipline: Discipline[];
  tiers: number[];
}

interface CompetencyLevelPerformance {
  statementId: number;
  competencyName: string;
  highestScoringMentee: string;
  highestScore: number;
  lowestScoringMentee: string;
  lowestScore: number;
  average: number;
}

export interface AverageCompetencyScoreAcrossDisciplinesTiers {
  statementId: number;
  competencyName: string;
  tier: number;
  disciplineId: number;
  averageScore: number;
}

// Discipline Interface
interface Discipline {
  disciplineId: number;
  discipline: string;
}
interface DisciplineAverage {
  disciplineId: number;
  discipline: string;
  average: number;
}
interface DisciplineLevelPerformance {
  disciplineId: number;
  discipline: string;
  highestScore: number;
  highestScoringMentee: string;
  lowestScore: number;
  lowestScoringMentee: string;
}

// Average Score Interface
interface AverageScoreAcrossDisciplines {
  disciplines: Discipline[];
  disciplineAverage: DisciplineAverage[];
  menteeDisciplinePerformance: any[]; // Type can be specified based on actual data
  disciplineLevelPerformance: DisciplineLevelPerformance[]; // Type can be specified based on actual data
}

// Feedback Deadlines Interface
interface TotalQuarterlyFeedbackDeadlinesMissedCount {
  mentorCount: number;
  menteeCount: number;
  mentees: string[];
  mentors: string[];
}
